// ============================================
// Contacts List Page
// ============================================

import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { toast } from 'sonner';
import { AppLayout } from '@/components/AppLayout';
import { TenantBadge } from '@/components/TenantBadge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api } from '@/lib/api';
import { DataTableSkeleton } from '@/components/skeletons';
import { EmptyState } from '@/components/ui/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { useTenant } from '@/hooks/useTenant';
import { buildTenantDisplayMap, getTenantDisplayById } from '@/lib/tenant-display';
import {
  Plus,
  Search,
  Loader2,
  Users,
  MoreVertical,
  Pencil,
  Star,
  Trash2,
  Mail,
  Phone,
} from 'lucide-react';

// Avatar colors for consistent coloring based on name
const avatarColors = [
  'bg-blue-100 text-blue-700',
  'bg-green-100 text-green-700',
  'bg-amber-100 text-amber-700',
  'bg-purple-100 text-purple-700',
  'bg-red-100 text-red-700',
  'bg-pink-100 text-pink-700',
];

// Get initials from name
const getInitials = (firstName: string, lastName: string) => {
  return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase() || 'U';
};

// Get avatar color based on name
const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

export function ContactsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const searchParams = useSearch({ from: '/contacts' });
  const { isTenantContextReady, activeTenantId, mode, tenantList } = useTenant();

  // State
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [accountFilter, setAccountFilter] = useState('');
  const [primaryFilter, setPrimaryFilter] = useState<'all' | 'primary'>('all');
  const [createModalOpen, setCreateModalOpen] = useState(() => searchParams.create === 'true');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState<{
    id: string;
    firstName: string;
    lastName: string;
    email?: string | null;
    phone?: string | null;
    mobile?: string | null;
    title?: string | null;
    department?: string | null;
    accountId: string;
    isPrimary?: boolean;
    notes?: string | null;
  } | null>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);

  // Clear search param from URL after modal opens
  useEffect(() => {
    if (searchParams.create === 'true') {
      navigate({ to: '/contacts', search: {}, replace: true });
    }
  }, [searchParams.create, navigate]);

  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    mobile: '',
    title: '',
    department: '',
    accountId: '',
    isPrimary: false,
    notes: '',
  });

  // Query accounts for dropdown
  const { data: accountsData } = useQuery({
    queryKey: ['accounts-list', mode, activeTenantId],
    queryFn: () => api.getAccounts({ pageSize: 100 }),
    enabled: isTenantContextReady,
  });

  // Query contacts
  const { data, isLoading, error } = useQuery({
    queryKey: ['contacts', mode, activeTenantId, page, search, accountFilter],
    queryFn: () =>
      api.getContacts({
        page,
        pageSize: 20,
        search: search || undefined,
        accountId: accountFilter || undefined,
      }),
    enabled: isTenantContextReady,
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: api.createContact.bind(api),
    onSuccess: () => {
      toast.success('Contact created successfully');
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setCreateModalOpen(false);
      resetForm();
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to create contact');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<typeof formData> }) =>
      api.updateContact(id, data),
    onSuccess: () => {
      toast.success('Contact updated successfully');
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setEditModalOpen(false);
      setSelectedContact(null);
      resetForm();
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to update contact');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.deleteContact(id),
    onSuccess: () => {
      toast.success('Contact deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['contacts'] });
      setDeleteModalOpen(false);
      setSelectedContact(null);
    },
    onError: error => {
      toast.error(error instanceof Error ? error.message : 'Failed to delete contact');
    },
  });

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      mobile: '',
      title: '',
      department: '',
      accountId: '',
      isPrimary: false,
      notes: '',
    });
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate({
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email || null,
      phone: formData.phone || null,
      mobile: formData.mobile || null,
      title: formData.title || null,
      department: formData.department || null,
      accountId: formData.accountId,
      isPrimary: formData.isPrimary,
      notes: formData.notes || null,
    });
  };

  const handleEdit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedContact) return;
    updateMutation.mutate({
      id: selectedContact.id,
      data: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email || undefined,
        phone: formData.phone || undefined,
        mobile: formData.mobile || undefined,
        title: formData.title || undefined,
        department: formData.department || undefined,
        isPrimary: formData.isPrimary,
        notes: formData.notes || undefined,
      },
    });
  };

  const handleDelete = () => {
    if (selectedContact) {
      deleteMutation.mutate(selectedContact.id);
    }
  };

  const openEditModal = (contact: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string | null;
    phone?: string | null;
    mobile?: string | null;
    title?: string | null;
    department?: string | null;
    accountId: string;
    isPrimary?: boolean;
    notes?: string | null;
  }) => {
    setSelectedContact(contact);
    setFormData({
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      email: contact.email || '',
      phone: contact.phone || '',
      mobile: contact.mobile || '',
      title: contact.title || '',
      department: contact.department || '',
      accountId: contact.accountId || '',
      isPrimary: contact.isPrimary || false,
      notes: contact.notes || '',
    });
    setEditModalOpen(true);
    setOpenDropdownId(null);
  };

  const openDeleteModal = (contact: {
    id: string;
    firstName: string;
    lastName: string;
    email?: string | null;
    phone?: string | null;
    mobile?: string | null;
    title?: string | null;
    department?: string | null;
    accountId: string;
    isPrimary?: boolean;
    notes?: string | null;
  }) => {
    setSelectedContact(contact);
    setDeleteModalOpen(true);
    setOpenDropdownId(null);
  };

  const togglePrimary = async (contact: { id: string; isPrimary?: boolean }) => {
    await updateMutation.mutateAsync({
      id: contact.id,
      data: { isPrimary: !contact.isPrimary },
    });
    setOpenDropdownId(null);
  };

  const contacts = data?.data || [];
  const pagination = data?.pagination;
  const accounts = accountsData?.data || [];
  const showTenantColumn = mode === 'all';
  const tenantDisplayMap = useMemo(() => buildTenantDisplayMap(tenantList), [tenantList]);

  // Filter contacts by primary status (client-side since API may not support this filter)
  const filteredContacts =
    primaryFilter === 'primary'
      ? contacts.filter((c: { isPrimary?: boolean }) => c.isPrimary)
      : contacts;

  // Count primary contacts
  const primaryCount = contacts.filter((c: { isPrimary?: boolean }) => c.isPrimary).length;

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Contacts</h1>
            <p className="text-muted-foreground mt-1">People associated with your accounts</p>
          </div>
          <Button onClick={() => setCreateModalOpen(true)}>
            <Plus className="h-4 w-4" />
            New Contact
          </Button>
        </div>

        {/* Filters & Search Bar */}
        <div className="bg-card rounded-xl border border-border">
          <div className="p-4 flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search contacts..."
                className="pl-10"
                value={search}
                onChange={e => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>

            {/* Filters */}
            <div className="flex gap-2">
              <Select
                value={accountFilter}
                onChange={e => {
                  setAccountFilter(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Accounts</option>
                {accounts.map((account: { id: string; name: string }) => (
                  <option key={account.id} value={account.id}>
                    {account.name}
                  </option>
                ))}
              </Select>
            </div>
          </div>

          {/* Quick Filters */}
          <div className="px-4 pb-4 flex flex-wrap gap-2">
            <button
              onClick={() => setPrimaryFilter('all')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                primaryFilter === 'all'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              All ({contacts.length})
            </button>
            <button
              onClick={() => setPrimaryFilter('primary')}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                primaryFilter === 'primary'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-muted/80'
              }`}
            >
              Primary ({primaryCount})
            </button>
          </div>
        </div>

        {/* Table (Desktop) */}
        <div className="hidden lg:block bg-card rounded-xl border border-border overflow-hidden">
          {isLoading ? (
            <div className="p-4">
              <DataTableSkeleton columnCount={showTenantColumn ? 7 : 6} rowCount={8} />
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <p className="text-destructive mb-2">Failed to load contacts</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            search || accountFilter || primaryFilter === 'primary' ? (
              <EmptyState
                title="No contacts found"
                description="Try adjusting your search or filters to find what you're looking for"
                actionLabel="Clear Query"
                onAction={() => {
                  setSearch('');
                  setAccountFilter('');
                  setPrimaryFilter('all');
                  setPage(1);
                }}
                icon={Search}
              />
            ) : (
              <EmptyState
                title="No contacts yet"
                description="Add contacts to track the people you do business with."
                actionLabel="Create Contact"
                onAction={() => setCreateModalOpen(true)}
                icon={Users}
              />
            )
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-5">
                    Name
                  </TableHead>
                  <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-5">
                    Account
                  </TableHead>
                  <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-5">
                    Title
                  </TableHead>
                  <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-5">
                    Email
                  </TableHead>
                  <TableHead className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-5">
                    Phone
                  </TableHead>
                  {showTenantColumn && (
                    <TableHead className="w-[180px] text-xs font-medium text-muted-foreground uppercase tracking-wider px-5">
                      Tenant
                    </TableHead>
                  )}
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContacts.map(
                  (contact: {
                    id: string;
                    firstName: string;
                    lastName: string;
                    email?: string | null;
                    phone?: string | null;
                    mobile?: string | null;
                    title?: string | null;
                    department?: string | null;
                    accountId: string;
                    accountName?: string;
                    isPrimary?: boolean;
                    notes?: string | null;
                    tenantId: string;
                  }) => {
                    const fullName = `${contact.firstName} ${contact.lastName}`;
                    const tenantDisplay = getTenantDisplayById(contact.tenantId, tenantDisplayMap);
                    return (
                      <TableRow
                        key={contact.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() =>
                          navigate({ to: '/contacts/$id', params: { id: contact.id } })
                        }
                      >
                        <TableCell className="px-5 py-4">
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-9 h-9 rounded-full flex items-center justify-center ${getAvatarColor(fullName)}`}
                            >
                              <span className="text-xs font-medium">
                                {getInitials(contact.firstName, contact.lastName)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-foreground">{fullName}</p>
                              {contact.isPrimary && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                                  Primary
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="px-5 py-4">
                          <span
                            className="text-sm text-primary hover:underline cursor-pointer"
                            onClick={e => {
                              e.stopPropagation();
                              if (contact.accountId) {
                                navigate({
                                  to: '/accounts/$id',
                                  params: { id: contact.accountId },
                                });
                              }
                            }}
                          >
                            {contact.accountName || '—'}
                          </span>
                        </TableCell>
                        <TableCell className="px-5 py-4 text-sm text-foreground">
                          {contact.title || '—'}
                        </TableCell>
                        <TableCell className="px-5 py-4 text-sm text-muted-foreground">
                          {contact.email || '—'}
                        </TableCell>
                        <TableCell className="px-5 py-4 text-sm text-muted-foreground">
                          {contact.phone || '—'}
                        </TableCell>
                        {showTenantColumn && (
                          <TableCell className="px-5 py-4">
                            <TenantBadge
                              tenantName={tenantDisplay.name}
                              brandColor={tenantDisplay.brandColor}
                              density="compact"
                            />
                          </TableCell>
                        )}
                        <TableCell className="px-5 py-4">
                          <div className="relative">
                            <button
                              onClick={e => {
                                e.stopPropagation();
                                setOpenDropdownId(
                                  openDropdownId === contact.id ? null : contact.id
                                );
                              }}
                              className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted"
                            >
                              <MoreVertical className="h-4 w-4" />
                            </button>
                            {openDropdownId === contact.id && (
                              <div
                                className="absolute right-0 top-full mt-1 w-48 bg-card rounded-lg shadow-lg border border-border py-1 z-10"
                                onClick={e => e.stopPropagation()}
                              >
                                <button
                                  onClick={() => openEditModal(contact)}
                                  className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted flex items-center gap-2"
                                >
                                  <Pencil className="h-4 w-4" />
                                  Edit Contact
                                </button>
                                <button
                                  onClick={() => togglePrimary(contact)}
                                  className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted flex items-center gap-2"
                                >
                                  <Star className="h-4 w-4" />
                                  {contact.isPrimary ? 'Remove Primary' : 'Set as Primary'}
                                </button>
                                <div className="border-t border-border my-1"></div>
                                <button
                                  onClick={() => openDeleteModal(contact)}
                                  className="w-full px-4 py-2 text-left text-sm text-destructive hover:bg-destructive/10 flex items-center gap-2"
                                >
                                  <Trash2 className="h-4 w-4" />
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  }
                )}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <div className="px-5 py-4 border-t border-border flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing{' '}
                <span className="font-medium text-foreground">
                  {(page - 1) * pagination.pageSize + 1}
                </span>{' '}
                to{' '}
                <span className="font-medium text-foreground">
                  {Math.min(page * pagination.pageSize, pagination.total)}
                </span>{' '}
                of <span className="font-medium text-foreground">{pagination.total}</span> contacts
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p - 1)}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={page === pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Mobile Cards */}
        <div className="lg:hidden space-y-3">
          {isLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton
                  key={i}
                  className="h-[140px] w-full rounded-xl bg-card border border-border"
                />
              ))}
            </div>
          ) : error ? (
            <div className="bg-card rounded-xl border border-border p-8 text-center">
              <p className="text-destructive">Failed to load contacts</p>
            </div>
          ) : filteredContacts.length === 0 ? (
            <div className="bg-card rounded-xl border border-border p-8 text-center">
              <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                <Users className="h-6 w-6 text-muted-foreground" />
              </div>
              <p className="text-foreground font-medium">No contacts found</p>
              <p className="text-sm text-muted-foreground mt-1">
                Try adjusting your search or filters
              </p>
            </div>
          ) : (
            filteredContacts.map(
              (contact: {
                id: string;
                firstName: string;
                lastName: string;
                email?: string | null;
                phone?: string | null;
                mobile?: string | null;
                title?: string | null;
                department?: string | null;
                accountId: string;
                accountName?: string;
                isPrimary?: boolean;
                notes?: string | null;
                tenantId: string;
              }) => {
                const fullName = `${contact.firstName} ${contact.lastName}`;
                const tenantDisplay = getTenantDisplayById(contact.tenantId, tenantDisplayMap);
                return (
                  <div
                    key={contact.id}
                    className="bg-card rounded-xl border border-border p-4 hover:border-primary/30 transition-colors"
                  >
                    <div
                      className="flex items-start gap-3"
                      onClick={() => navigate({ to: '/contacts/$id', params: { id: contact.id } })}
                    >
                      <div
                        className={`w-12 h-12 rounded-full flex items-center justify-center shrink-0 ${getAvatarColor(fullName)}`}
                      >
                        <span className="text-sm font-medium">
                          {getInitials(contact.firstName, contact.lastName)}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-foreground">{fullName}</p>
                              {contact.isPrimary && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-amber-100 text-amber-700">
                                  Primary
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              {contact.title || 'No title'}
                            </p>
                          </div>
                        </div>
                        <p className="mt-1 text-sm text-primary">
                          {contact.accountName || 'No account'}
                        </p>
                        {showTenantColumn && (
                          <div className="mt-2">
                            <TenantBadge
                              tenantName={tenantDisplay.name}
                              brandColor={tenantDisplay.brandColor}
                              className="max-w-[12rem]"
                            />
                          </div>
                        )}
                        <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
                          {contact.email && (
                            <div className="flex items-center gap-2">
                              <Mail className="h-3.5 w-3.5" />
                              <span>{contact.email}</span>
                            </div>
                          )}
                          {contact.phone && (
                            <div className="flex items-center gap-2">
                              <Phone className="h-3.5 w-3.5" />
                              <span>{contact.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    {/* Mobile Action Buttons */}
                    <div className="mt-3 pt-3 border-t border-border flex items-center justify-end gap-2">
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          openEditModal(contact);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-foreground bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </button>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          togglePrimary(contact);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-amber-700 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors"
                      >
                        <Star className="h-3.5 w-3.5" />
                        {contact.isPrimary ? 'Unset' : 'Primary'}
                      </button>
                      <button
                        onClick={e => {
                          e.stopPropagation();
                          openDeleteModal(contact);
                        }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-destructive bg-destructive/10 rounded-lg hover:bg-destructive/20 transition-colors"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>
                );
              }
            )
          )}
        </div>
      </div>

      {/* Create Contact Modal */}
      <Dialog open={createModalOpen} onClose={() => setCreateModalOpen(false)}>
        <DialogContent onClose={() => setCreateModalOpen(false)} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>New Contact</DialogTitle>
            <DialogDescription>Add a new contact to an account</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreate}>
            <div className="p-6 pt-0 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Contact Information Section */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Contact Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="First Name"
                    required
                    placeholder="John"
                    value={formData.firstName}
                    onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                  />
                  <Input
                    label="Last Name"
                    required
                    placeholder="Smith"
                    value={formData.lastName}
                    onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                  />
                  <Input
                    label="Email"
                    type="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                  />
                  <Input
                    label="Phone"
                    placeholder="+1 (555) 123-4567"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  />
                  <Input
                    label="Mobile"
                    placeholder="+1 (555) 987-6543"
                    value={formData.mobile}
                    onChange={e => setFormData({ ...formData, mobile: e.target.value })}
                  />
                </div>
              </div>

              {/* Work Information Section - Create Modal */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Work Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select
                    label="Account"
                    required
                    value={formData.accountId}
                    onChange={e => setFormData({ ...formData, accountId: e.target.value })}
                  >
                    <option value="">Select account</option>
                    {accounts.map((account: { id: string; name: string }) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </Select>
                  <Input
                    label="Job Title"
                    placeholder="CEO, VP of Sales, etc."
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                  />
                  <Input
                    label="Department"
                    placeholder="Sales, Marketing, etc."
                    value={formData.department}
                    onChange={e => setFormData({ ...formData, department: e.target.value })}
                  />
                </div>
              </div>

              {/* Options Section */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Options
                </h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isPrimary}
                    onChange={e => setFormData({ ...formData, isPrimary: e.target.checked })}
                    className="w-4 h-4 rounded border-input text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">
                    Set as primary contact for this account
                  </span>
                </label>
                <p className="text-xs text-muted-foreground mt-2">
                  Primary contacts are the main point of contact for an account.
                </p>
              </div>

              {/* Notes Section */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Notes
                </h3>
                <Textarea
                  rows={3}
                  placeholder="Additional notes about this contact..."
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter className="bg-muted/30">
              <Button type="button" variant="outline" onClick={() => setCreateModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Create Contact
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Contact Modal */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)}>
        <DialogContent onClose={() => setEditModalOpen(false)} className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit Contact</DialogTitle>
            <DialogDescription>Update contact information</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEdit}>
            <div className="p-6 pt-0 space-y-6 max-h-[calc(100vh-200px)] overflow-y-auto">
              {/* Contact Information Section */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Contact Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input
                    label="First Name"
                    required
                    placeholder="John"
                    value={formData.firstName}
                    onChange={e => setFormData({ ...formData, firstName: e.target.value })}
                  />
                  <Input
                    label="Last Name"
                    required
                    placeholder="Smith"
                    value={formData.lastName}
                    onChange={e => setFormData({ ...formData, lastName: e.target.value })}
                  />
                  <Input
                    label="Email"
                    type="email"
                    placeholder="john@company.com"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                  />
                  <Input
                    label="Phone"
                    placeholder="+1 (555) 123-4567"
                    value={formData.phone}
                    onChange={e => setFormData({ ...formData, phone: e.target.value })}
                  />
                  <Input
                    label="Mobile"
                    placeholder="+1 (555) 987-6543"
                    value={formData.mobile}
                    onChange={e => setFormData({ ...formData, mobile: e.target.value })}
                  />
                </div>
              </div>

              {/* Work Information Section - Edit Modal */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                  Work Information
                </h3>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Select
                    label="Account"
                    required
                    value={formData.accountId}
                    onChange={e => setFormData({ ...formData, accountId: e.target.value })}
                  >
                    <option value="">Select account</option>
                    {accounts.map((account: { id: string; name: string }) => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </Select>
                  <Input
                    label="Job Title"
                    placeholder="CEO, VP of Sales, etc."
                    value={formData.title}
                    onChange={e => setFormData({ ...formData, title: e.target.value })}
                  />
                  <Input
                    label="Department"
                    placeholder="Sales, Marketing, etc."
                    value={formData.department}
                    onChange={e => setFormData({ ...formData, department: e.target.value })}
                  />
                </div>
              </div>

              {/* Options Section */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Options
                </h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isPrimary}
                    onChange={e => setFormData({ ...formData, isPrimary: e.target.checked })}
                    className="w-4 h-4 rounded border-input text-primary focus:ring-primary"
                  />
                  <span className="text-sm text-foreground">
                    Set as primary contact for this account
                  </span>
                </label>
                <p className="text-xs text-muted-foreground mt-2">
                  Primary contacts are the main point of contact for an account.
                </p>
              </div>

              {/* Notes Section */}
              <div>
                <h3 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                  <svg
                    className="w-4 h-4 text-muted-foreground"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  Notes
                </h3>
                <Textarea
                  rows={3}
                  placeholder="Additional notes about this contact..."
                  value={formData.notes}
                  onChange={e => setFormData({ ...formData, notes: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter className="bg-muted/30">
              <Button type="button" variant="outline" onClick={() => setEditModalOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={updateMutation.isPending}>
                {updateMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                Update Contact
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
        <DialogContent onClose={() => setDeleteModalOpen(false)} className="max-w-md">
          <DialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
              <Trash2 className="h-6 w-6 text-destructive" />
            </div>
            <DialogTitle className="text-center">Delete Contact</DialogTitle>
            <DialogDescription className="text-center">
              Are you sure you want to delete{' '}
              <span className="font-medium text-foreground">
                {selectedContact?.firstName} {selectedContact?.lastName}
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="sm:justify-center">
            <Button type="button" variant="outline" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              Delete Contact
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
